import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../context/I18nContext";
import { getAvatarUrl, getInitials } from "../../utils/avatar";
import useUnreadMessages from "../../utils/useUnreadMessages";
import "./Sidebar.css";

const BASE_LINKS = [
  { to: "/dashboard", labelKey: "sidebar.links.dashboard" },
  { to: "/lessons", labelKey: "sidebar.links.lessons" },
  { to: "/resources", labelKey: "sidebar.links.resources" },
  { to: "/contact", labelKey: "sidebar.links.contact" },
  { to: "/profile", labelKey: "sidebar.links.profile" },
];

const ICONS_BY_TO = {
  "/dashboard": "fa-solid fa-border-all",
  "/lessons": "fa-regular fa-calendar",
  "/timetable": "fa-solid fa-calendar-days",
  "/resources": "fa-solid fa-folder-open",
  "/users": "fa-solid fa-users",
  "/admin/students": "fa-solid fa-user-graduate",
  "/admin/timetable": "fa-solid fa-calendar-days",
  "/admin/pending-teachers": "fa-solid fa-user-clock",
  "/admin/classes": "fa-solid fa-chalkboard-user",
  "/packs": "fa-solid fa-box",
  "/admin/payments": "fa-solid fa-credit-card",
  "/admin/teachers": "fa-solid fa-chalkboard-teacher",
  "/admin/support": "fa-regular fa-life-ring",
  "/tests": "fa-regular fa-clipboard",
  "/messages": "fa-regular fa-comments",
  "/homeworks": "fa-solid fa-book",
  "/contact": "fa-regular fa-envelope",
  "/profile": "fa-regular fa-user",
  "/admin": "fa-solid fa-gauge",
  "/students": "fa-solid fa-user-graduate",
  "/lessons/new": "fa-solid fa-plus",
  "/programs": "fa-solid fa-layer-group",
};

function buildSections(links) {
  const isIn = (to, list) => list.includes(to);
  const main = links.filter((l) =>
    isIn(l.to, [
      "/dashboard",
      "/lessons",
      "/messages",
      "/tests",
      "/homeworks",
      "/contact",
    ]),
  );
  const management = links.filter((l) =>
    isIn(l.to, ["/admin", "/students", "/lessons/new"]),
  );
  const other = links.filter((l) => isIn(l.to, ["/profile"]));

  const remainder = links.filter(
    (l) =>
      !main.some((x) => x.to === l.to) &&
      !management.some((x) => x.to === l.to) &&
      !other.some((x) => x.to === l.to),
  );

  const sections = [];
  if (main.length)
    sections.push({
      key: "main",
      titleKey: "sidebar.sections.main",
      links: main,
    });
  if (management.length)
    sections.push({
      key: "mgmt",
      titleKey: "sidebar.sections.management",
      links: management,
    });
  if (other.length || remainder.length)
    sections.push({
      key: "other",
      titleKey: "sidebar.sections.other",
      links: [...other, ...remainder],
    });
  return sections;
}

function buildLinks(role) {
  const adminRoles = ["admin", "superadmin", "support", "accounting"];
  const roleLinks = {
    admin: [
      { to: "/messages", labelKey: "sidebar.links.messages" },
      { to: "/tests", labelKey: "sidebar.links.tests" },
      { to: "/homeworks", labelKey: "sidebar.links.homeworks" },
    ],
    superadmin: [
      { to: "/messages", labelKey: "sidebar.links.messages" },
      { to: "/tests", labelKey: "sidebar.links.tests" },
      { to: "/homeworks", labelKey: "sidebar.links.homeworks" },
    ],
    support: [{ to: "/messages", labelKey: "sidebar.links.messages" }],
    accounting: [],
    teacher: [
      { to: "/messages", labelKey: "sidebar.links.messages" },
      { to: "/lessons/new", labelKey: "sidebar.links.newLesson" },
      { to: "/students", labelKey: "sidebar.links.myStudents" },
      { to: "/tests", labelKey: "sidebar.links.tests" },
      { to: "/homeworks", labelKey: "sidebar.links.homeworks" },
    ],
    parent: [
      { to: "/students", labelKey: "sidebar.links.myChildren" },
      { to: "/messages", labelKey: "sidebar.links.messages" },
    ],
    student: [{ to: "/lessons", labelKey: "sidebar.links.myLessons" }],
  };

  const baseLinks = adminRoles.includes(role)
    ? BASE_LINKS.filter((l) => l.to !== "/lessons")
    : BASE_LINKS;

  const combined = [...baseLinks, ...(roleLinks[role] || [])];
  const seen = new Set();
  return combined.filter((link) => {
    if (seen.has(link.to)) return false;
    seen.add(link.to);
    return true;
  });
}

export default function Sidebar({
  isOpen = false,
  onClose,
  collapsed = false,
  onToggleCollapsed,
}) {
  const { user, effectiveRole, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const labelForKey = (key) => {
    const defaultValue =
      key === "sidebar.links.homeworks" ? "Homeworks" : undefined;
    return t(key, defaultValue ? { defaultValue } : undefined);
  };

  const links = useMemo(() => buildLinks(effectiveRole), [effectiveRole]);

  const { unread: unreadMessages } = useUnreadMessages(location.pathname);
  const sections = useMemo(() => buildSections(links), [links]);

  const roleKey = `sidebar.role.${effectiveRole}`;
  const roleLabel = t(roleKey);
  const resolvedRole =
    roleLabel === roleKey ? t("sidebar.role.user") : roleLabel;

  const isAdminRole = ["admin", "superadmin", "support", "accounting"].includes(
    effectiveRole,
  );

  const dashboardTabs = useMemo(() => {
    if (isAdminRole) {
      return [
        { to: "/dashboard", labelKey: "adminPage.tabs.overview" },
        { to: "/users", labelKey: "adminPage.tabs.users" },
        { to: "/admin/students", labelKey: "adminPage.tabs.students" },
        { to: "/admin/timetable", labelKey: "adminPage.tabs.timetable" },
        {
          to: "/admin/pending-teachers",
          labelKey: "adminPage.tabs.pendingTeachers",
        },
        { to: "/admin/classes", labelKey: "adminPage.tabs.classes" },
        { to: "/packs", labelKey: "adminPage.tabs.packs" },
        { to: "/admin/payments", labelKey: "adminPage.tabs.payments" },
        { to: "/admin/teachers", labelKey: "adminPage.tabs.teachers" },
        { to: "/admin/support", labelKey: "adminPage.tabs.support" },
      ];
    }

    if (effectiveRole === "teacher") {
      return [
        { to: "/dashboard", labelKey: "sidebar.links.dashboard" },
        { to: "/lessons", labelKey: "sidebar.links.lessons" },
        { to: "/timetable", labelKey: "sidebar.links.timetable" },
        { to: "/resources", labelKey: "sidebar.links.resources" },
        { to: "/lessons/new", labelKey: "sidebar.links.newLesson" },
        { to: "/students", labelKey: "sidebar.links.myStudents" },
        { to: "/tests", labelKey: "sidebar.links.tests" },
        { to: "/homeworks", labelKey: "sidebar.links.homeworks" },
      ];
    }

    if (effectiveRole === "parent") {
      return [
        { to: "/dashboard", labelKey: "sidebar.links.dashboard" },
        { to: "/students", labelKey: "sidebar.links.myChildren" },
        { to: "/lessons", labelKey: "sidebar.links.lessons" },
        { to: "/resources", labelKey: "sidebar.links.resources" },
        { to: "/profile", labelKey: "sidebar.links.profile" },
      ];
    }

    return [];
  }, [effectiveRole, isAdminRole]);

  const isAdminAreaPath = useMemo(() => {
    if (!isAdminRole) return false;

    const path = location.pathname;
    if (path === "/dashboard" || path.startsWith("/dashboard/")) return true;
    if (path === "/users") return true;
    if (path === "/packs" || path.startsWith("/packs/")) return true;
    if (path.startsWith("/admin/")) return true;
    return false;
  }, [isAdminRole, location.pathname]);

  const isOnDashboard = isAdminRole
    ? isAdminAreaPath
    : location.pathname === "/dashboard" ||
      location.pathname.startsWith("/dashboard/");

  const hasDashboardDropdown = dashboardTabs.length > 0;
  const isInDashboardDropdown = useMemo(() => {
    if (!hasDashboardDropdown) return false;
    if (isAdminRole) {
      return (
        isAdminAreaPath ||
        location.pathname === "/programs" ||
        location.pathname.startsWith("/programs/")
      );
    }

    return dashboardTabs.some((tab) => {
      const to = tab.to;
      if (!to) return false;
      return (
        (to === "/dashboard" && isOnDashboard) ||
        location.pathname === to ||
        location.pathname.startsWith(`${to}/`)
      );
    });
  }, [
    dashboardTabs,
    hasDashboardDropdown,
    isAdminRole,
    isAdminAreaPath,
    isOnDashboard,
    location.pathname,
  ]);

  const [dashboardExpanded, setDashboardExpanded] = useState(
    isInDashboardDropdown,
  );

  useEffect(() => {
    if (isInDashboardDropdown) setDashboardExpanded(true);
  }, [isInDashboardDropdown]);

  if (!user) return null;

  const handleClose = () => {
    if (typeof onClose === "function") onClose();
  };

  return (
    <aside
      id="app-sidebar"
      className={`app-sidebar${isOpen ? " app-sidebar--open" : ""}${
        collapsed ? " app-sidebar--collapsed" : ""
      }`}
      aria-label={t("nav.home")}
    >
      <div className="app-sidebar__inner">
        <button
          type="button"
          className="app-sidebar__close"
          aria-label={t("sidebar.close")}
          onClick={handleClose}
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className="app-sidebar__brand">
          <img src="/logo.png" alt="Casaclass" className="app-sidebar__logo" />
          <img
            src="/logo%20icon.png"
            alt=""
            aria-hidden="true"
            className="app-sidebar__logo-icon"
          />
          {typeof onToggleCollapsed === "function" ? (
            <button
              type="button"
              className="app-sidebar__collapse"
              aria-label={t(collapsed ? "sidebar.expand" : "sidebar.collapse")}
              title={t(collapsed ? "sidebar.expand" : "sidebar.collapse")}
              aria-pressed={collapsed}
              onClick={onToggleCollapsed}
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
          ) : null}
        </div>

        <header className="app-sidebar__header">
          <div className="app-sidebar__avatar">
            {getAvatarUrl(user) ? (
              <img src={getAvatarUrl(user)} alt="" loading="lazy" />
            ) : (
              getInitials(user)
            )}
          </div>
          <div className="app-sidebar__who">
            <div className="app-sidebar__name">
              {user.first_name || user.full_name || user.email}
            </div>
            <div className="app-sidebar__role">{resolvedRole}</div>
          </div>
        </header>

        <nav className="app-sidebar__nav" aria-label={t("nav.home")}>
          {sections.map((section) => (
            <div className="app-sidebar__section" key={section.key}>
              <div className="app-sidebar__section-title">
                {t(section.titleKey)}
              </div>
              <div className="app-sidebar__section-links">
                {section.links.map((link) => {
                  if (link.to === "/dashboard" && hasDashboardDropdown) {
                    return (
                      <div className="app-sidebar__dropdown" key={link.to}>
                        <div
                          className={`app-sidebar__link app-sidebar__link--dropdown${
                            isOnDashboard ? " is-active" : ""
                          }`}
                        >
                          <NavLink
                            to="/dashboard"
                            className="app-sidebar__dropdown-main"
                            onClick={handleClose}
                          >
                            <span
                              className="app-sidebar__icon"
                              aria-hidden="true"
                            >
                              <i
                                className={
                                  ICONS_BY_TO[link.to] || "fa-regular fa-circle"
                                }
                              ></i>
                            </span>
                            <span className="app-sidebar__label">
                              {labelForKey(link.labelKey)}
                            </span>
                          </NavLink>

                          <button
                            type="button"
                            className="app-sidebar__dropdown-toggle"
                            aria-label={labelForKey(link.labelKey)}
                            aria-expanded={dashboardExpanded}
                            onClick={() => setDashboardExpanded((v) => !v)}
                          >
                            <i
                              className={`fa-solid fa-chevron-down${
                                dashboardExpanded ? " is-open" : ""
                              }`}
                              aria-hidden="true"
                            ></i>
                          </button>
                        </div>

                        {dashboardExpanded ? (
                          <div className="app-sidebar__sublinks">
                            {dashboardTabs.map((tab) => {
                              const to = tab.to;
                              if (!to) return null;
                              const isActive =
                                (to === "/dashboard" &&
                                  (location.pathname === "/dashboard" ||
                                    location.pathname.startsWith(
                                      "/dashboard/",
                                    ))) ||
                                (to === "/packs" &&
                                  (location.pathname === "/packs" ||
                                    location.pathname.startsWith("/packs/") ||
                                    location.pathname.startsWith(
                                      "/dashboard/packs/",
                                    ))) ||
                                location.pathname === to ||
                                location.pathname.startsWith(`${to}/`);

                              return (
                                <Link
                                  key={to}
                                  to={to}
                                  className={`app-sidebar__sublink${
                                    isActive ? " is-active" : ""
                                  }`}
                                  onClick={handleClose}
                                >
                                  {t(tab.labelKey)}
                                </Link>
                              );
                            })}

                            {isAdminRole ? (
                              <Link
                                to="/programs"
                                className={`app-sidebar__sublink${
                                  location.pathname === "/programs" ||
                                  location.pathname.startsWith("/programs/")
                                    ? " is-active"
                                    : ""
                                }`}
                                onClick={handleClose}
                              >
                                {t("sidebar.links.programs")}
                              </Link>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  }

                  return (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className={({ isActive }) =>
                        `app-sidebar__link${isActive ? " is-active" : ""}`
                      }
                      onClick={handleClose}
                    >
                      <span className="app-sidebar__icon" aria-hidden="true">
                        <i
                          className={
                            ICONS_BY_TO[link.to] || "fa-regular fa-circle"
                          }
                        ></i>
                      </span>
                      <span className="app-sidebar__label">
                        {labelForKey(link.labelKey)}
                      </span>
                      {link.to === "/messages" && unreadMessages > 0 ? (
                        <span className="app-sidebar__badge">
                          {unreadMessages > 99 ? "99+" : unreadMessages}
                        </span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <button
          type="button"
          className="app-sidebar__logout"
          onClick={() => {
            handleClose();
            logout();
          }}
        >
          <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true"></i>
          <span className="app-sidebar__label">{t("sidebar.logout")}</span>
        </button>
      </div>
    </aside>
  );
}
